import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, AlertCircle, LayoutTemplate } from 'lucide-react';
import { toast } from 'sonner';
import { withBackend } from '../utils/api';

// Slides are authored at a fixed 1280x720 canvas; tiles scale that canvas
// down to fit the grid cell width.
const SLIDE_WIDTH = 1280;
const SLIDE_HEIGHT = 720;

export default function TemplateSlidesModal({ templateId, templateName, userId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [template, setTemplate] = useState(null);
  // One shared scale for every tile: all tiles have the same width in the
  // grid, so we measure a single representative container and reuse it.
  const [scale, setScale] = useState(0);
  const firstTileRef = useRef(null);

  // Fetch the template detail (slides included).
  useEffect(() => {
    if (!templateId) return undefined;
    const controller = new AbortController();
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(
          withBackend(`/api/deck-templates/${templateId}?user_id=${encodeURIComponent(userId || '')}`),
          { signal: controller.signal }
        );
        let data = null;
        try {
          data = await res.json();
        } catch (parseErr) {
          data = null;
        }
        if (!res.ok) {
          throw new Error((data && data.error) || `Failed to load template (${res.status})`);
        }
        if (!cancelled) {
          setTemplate((data && data.template) || null);
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        const message = err.message || 'Failed to load template slides';
        if (!cancelled) {
          setError(message);
          toast.error(message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [templateId, userId]);

  // Measure the first tile and derive the shared iframe scale. Re-runs when
  // slides arrive (the ref only exists once the grid renders).
  useEffect(() => {
    const node = firstTileRef.current;
    if (!node || typeof ResizeObserver === 'undefined') {
      if (node) setScale(node.clientWidth / SLIDE_WIDTH);
      return undefined;
    }
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect ? entry.contentRect.width : entry.target.clientWidth;
        if (width > 0) setScale(width / SLIDE_WIDTH);
      }
    });
    observer.observe(node);
    setScale(node.clientWidth / SLIDE_WIDTH);
    return () => observer.disconnect();
  }, [template]);

  // Escape closes.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose && onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const handleBackdropClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget && onClose) onClose();
    },
    [onClose]
  );

  const slides = (template && Array.isArray(template.slides) ? template.slides : []);
  const displayName = (template && template.name) || templateName || 'Deck template';
  const slideCount = template
    ? (typeof template.slide_count === 'number' ? template.slide_count : slides.length)
    : null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label={`Slides in ${displayName}`}
    >
      <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-border shrink-0">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-foreground truncate flex items-center gap-2">
              <LayoutTemplate className="w-5 h-5 shrink-0 text-muted-foreground" />
              <span className="truncate">{displayName}</span>
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              {slideCount === null
                ? 'Browsing saved template slides'
                : `${slideCount} slide${slideCount === 1 ? '' : 's'}`}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {loading && (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="space-y-2">
                  <div className="w-full rounded-lg bg-muted animate-pulse" style={{ paddingBottom: '56.25%' }} />
                  <div className="h-3 w-2/3 rounded bg-muted animate-pulse" />
                </div>
              ))}
            </div>
          )}

          {!loading && error && (
            <div className="flex flex-col items-center justify-center text-center py-12 gap-3">
              <AlertCircle className="w-8 h-8 text-destructive" />
              <p className="text-sm font-medium text-foreground">Couldn't load this template</p>
              <p className="text-sm text-muted-foreground max-w-sm">{error}</p>
            </div>
          )}

          {!loading && !error && slides.length === 0 && (
            <div className="flex flex-col items-center justify-center text-center py-12 gap-3">
              <LayoutTemplate className="w-8 h-8 text-muted-foreground" />
              <p className="text-sm font-medium text-foreground">No slides in this template</p>
              <p className="text-sm text-muted-foreground max-w-sm">
                This template doesn't contain any saved slides yet.
              </p>
            </div>
          )}

          {!loading && !error && slides.length > 0 && (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              {slides.map((slide, i) => {
                const number = typeof slide.index === 'number' ? slide.index + 1 : i + 1;
                return (
                  <div key={`${number}-${i}`} className="min-w-0">
                    <div
                      ref={i === 0 ? firstTileRef : null}
                      className="rounded-lg border border-border bg-muted overflow-hidden"
                      style={{ position: 'relative', paddingBottom: '56.25%' }}
                    >
                      {/* Screenshot FIRST: sandboxed iframes can't run the
                          slides' entrance animations or charts, so live HTML
                          renders half-invisible. The stored preview is what
                          the slide actually looks like. */}
                      {slide.preview_url ? (
                        <img
                          src={slide.preview_url}
                          alt={slide.title || `Slide ${number}`}
                          loading="lazy"
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      ) : slide.html_source ? (
                        scale > 0 && (
                          <iframe
                            title={`Slide ${number}`}
                            srcDoc={slide.html_source}
                            sandbox="allow-same-origin"
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: `${SLIDE_WIDTH}px`,
                              height: `${SLIDE_HEIGHT}px`,
                              transform: `scale(${scale})`,
                              transformOrigin: 'top left',
                              pointerEvents: 'none',
                              border: 'none',
                            }}
                          />
                        )
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground">
                          No preview
                        </div>
                      )}
                    </div>
                    <div className="mt-2 flex items-center gap-2 min-w-0">
                      <span className="text-xs font-medium text-muted-foreground shrink-0">{number}</span>
                      <span className="text-sm text-foreground truncate flex-1">
                        {slide.title || `Slide ${number}`}
                      </span>
                      {slide.slide_type && (
                        <span className="shrink-0 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground border border-border">
                          {slide.slide_type}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
